/**
 * Labstep
 *
 * @module components/FilterPost/NewIndex/MetadataExperimentWorkflow/Form
 * @desc Filter Metadata form
 */

import { withActiveGroup } from 'labstep-web/containers/ActiveGroup';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import { FormReusableGroup } from 'labstep-web/core/Form/Reusable/Group';
import { SearchSelectContainer as SearchSelect } from 'labstep-web/core/Select/Search';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import {
  PostFilterComparison,
  PostFilterComparisonHumanReadable,
  PostFilterComparisonHumanReadableChronologic,
} from 'labstep-web/services/postFilter/constants';
import {
  getPostFilterMetadatasDate,
  getPostFilterMetadatasDatetime,
  getPostFilterMetadatasDefault,
  getPostFilterMetadatasNumeric,
  getPostFilterMetadatasOptions,
} from 'labstep-web/services/postFilter/filters/metadatas';
import type { PostFilterMetadatas } from 'labstep-web/services/postFilter/metadatas';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { fieldsPerMetadataType } from './fields';
import type { FilterPostNewIndexMetadataExperimentWorkflowFormProps } from './types';

const getPostFilterByPath = (path: string) => ({
  [MetadataType.default]: getPostFilterMetadatasDefault(path),
  [MetadataType.numeric]: getPostFilterMetadatasNumeric(path),
  [MetadataType.date]: getPostFilterMetadatasDate(path),
  [MetadataType.datetime]: getPostFilterMetadatasDatetime(path),
  [MetadataType.options]: getPostFilterMetadatasOptions(path),
});

const getPostFilterDefaultValue = (typeValue: MetadataType) => {
  const defaultValues: Record<
    MetadataType,
    { value: any; label: any }
  > = {
    [MetadataType.default]: {
      value: PostFilterComparison.eq,
      label: PostFilterComparisonHumanReadable.eq,
    },
    [MetadataType.numeric]: {
      value: PostFilterComparison.eq,
      label: PostFilterComparisonHumanReadable.eq,
    },
    [MetadataType.date]: {
      value: PostFilterComparison.lt,
      label: PostFilterComparisonHumanReadableChronologic.lt,
    },
    [MetadataType.datetime]: {
      value: PostFilterComparison.lt,
      label: PostFilterComparisonHumanReadableChronologic.lt,
    },
    [MetadataType.options]: {
      value: PostFilterComparison.contains,
      label: PostFilterComparisonHumanReadable.contains,
    },
    [MetadataType.file]: {
      value: PostFilterComparison.not_null,
      label: PostFilterComparisonHumanReadable.not_null,
    },
    [MetadataType.sequence]: {
      value: PostFilterComparison.eq,
      label: PostFilterComparisonHumanReadable.eq,
    },
    [MetadataType.entity_relation]: {
      value: PostFilterComparison.eq,
      label: PostFilterComparisonHumanReadable.eq,
    },
    [MetadataType.molecule]: {
      value: PostFilterComparison.eq,
      label: PostFilterComparisonHumanReadable.eq,
    },
  };

  return defaultValues[typeValue];
};

const FILTERS: Record<string, Record<string, PostFilterMetadatas>> = {
  experiments_metadatas: getPostFilterByPath('experiments.metadatas'),
};

const FilterPostNewIndexMetadataExperimentWorkflowForm: React.FC<
  FilterPostNewIndexMetadataExperimentWorkflowFormProps
> = ({
  addPostFilter,
  activeGroup,
  onDone,
  defaultValues: defaultValuesProps,
  replace,
}) => {
  const [metadata, setMetadata] = useState<Metadata | null>(null);

  const fields = useMemo(() => {
    if (metadata) {
      return [
        ...fieldsPerMetadataType(!!defaultValuesProps)[metadata.type]
          .fields,
      ];
    }
    return [];
  }, [metadata]);

  const onSubmit = useCallback(
    (values): void => {
      if (!metadata) {
        return;
      }

      const { value, comparison } = values;

      const typeValue = metadata.type as
        | MetadataType.default
        | MetadataType.numeric
        | MetadataType.date
        | MetadataType.datetime
        | MetadataType.options;

      if (typeValue === MetadataType.numeric) {
        values.value = Number(value);
      }
      const filter = FILTERS.experiments_metadatas[typeValue];
      addPostFilter(
        filter.getNode([
          metadata.label || '',
          [value, comparison?.value || PostFilterComparison.eq],
        ]),
        true,
      );
      onDone();
    },
    [addPostFilter, metadata],
  );

  const defaultValues = {};

  return (
    <>
      <ParamsStateContextProvider value={{}}>
        <SearchSelect
          autoFocus
          openMenuOnFocus
          entityName={Metadata.entityName}
          labelKey="label"
          searchKey="label_like"
          params={{
            permission_class: ExperimentWorkflow.entityName,
            group_id: activeGroup?.id,
            group_by_label: 1,
            type_not: MetadataType.file,
            sort: 'label',
            count: 8,
          }}
          setOptions={(metadatas: Metadata[]) =>
            metadatas.map((metadata: Metadata) => {
              return { label: metadata.label, value: metadata };
            })
          }
          onChange={(selectedOption) => {
            if (selectedOption) {
              setMetadata(selectedOption.value as Metadata);
            }
          }}
        />
      </ParamsStateContextProvider>
      <FormReusableGroup
        fields={fields}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        onDone={onDone}
        addAnother={false}
      />
    </>
  );
};

export default withActiveGroup(
  FilterPostNewIndexMetadataExperimentWorkflowForm,
);
